import i18n from "i18next";

import { Dispatch, JSX, SetStateAction, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Spinner from "@/components/helpers/Spinner/Spinner.tsx";
import { IconEmptyUser, IconPencil, IconProfileSelectLock } from "@/components/icons";

import { Profile } from "@/api/auth/auth.types.ts";
import { ROUTES } from "@/config/routes.ts";
import { useJwtCreate } from "@/hooks/api/login.hooks.ts";
import { cn } from "@/utils/cn.ts";

import { ProfileTabs } from "../gate/profiles/types";

interface ProfileCardProps extends Pick<Profile, "id" | "picture" | "name" | "age" | "is_lock" | "interface_lang"> {
  setProfileId: Dispatch<SetStateAction<number>>;
  setTab: Dispatch<SetStateAction<ProfileTabs>>;
  setIsEdit: Dispatch<SetStateAction<boolean>>;
}

const ProfileCard = ({
  age,
  id,
  name,
  picture,
  interface_lang,
  is_lock,
  setProfileId,
  setTab,
  setIsEdit,
}: ProfileCardProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<{ enter: boolean; edit: boolean }>({ enter: false, edit: false });
  const { mutate } = useJwtCreate({ profileId: id.toString(), age: age ? age.toString() : "18" });

  const handleClick = () => {
    if (!is_lock) {
      setIsLoading((prevState) => ({ ...prevState, enter: true }));
      mutate(
        { profile_id: id.toString() },
        {
          onSuccess: () => {
            navigate(ROUTES.MAIN.MAIN);
            if (interface_lang) {
              i18n.changeLanguage(interface_lang);
            }
          },
          onError: () => {
            setIsLoading((prevState) => ({ ...prevState, enter: false }));
          },
        },
      );
    } else {
      setProfileId(id);
      setTab(ProfileTabs.OPEN_PIN);
      setIsEdit(false);
    }
  };

  const handleEditClick = () => {
    if (!is_lock) {
      setIsLoading((prevState) => ({ ...prevState, edit: true }));
      mutate(
        { profile_id: id.toString() },
        {
          onSuccess: () => {
            navigate(ROUTES.GATE.UPDATE_PROFILE.replace(":id", id.toString()));
          },
          onError: () => {
            setIsLoading((prevState) => ({ ...prevState, edit: false }));
          },
        },
      );
    } else {
      setIsEdit(true);
      setProfileId(id);
      setTab(ProfileTabs.OPEN_PIN);
    }
  };

  const getGradient = useMemo(() => {
    if (!picture || !picture.color) {
      return "linear-gradient(rgba(255, 75, 0, 0.6) 0%, rgba(255, 75, 0, 0.25) 100%)";
    }
    const bigint = parseInt(picture.color.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `linear-gradient(rgba(${r}, ${g}, ${b}, 0.6) 0%, rgba(${r}, ${g}, ${b}, 0.25) 100%)`;
  }, [picture]);

  return (
    <>
      <div
        style={{
          background: getGradient ?? "linear-gradient(to bottom right, #F87D49, #5B1C01)",
        }}
        className={cn(
          "relative aspect-[265/180] overflow-clip rounded-5 p-3",
          "max-lg:aspect-auto max-lg:h-52 max-lg:w-full max-sm:h-36",
        )}
      >
        <button type={"button"} onClick={handleClick} className={"flex h-full w-full flex-col gap-2"}>
          <span className={"flex items-center gap-2"}>
            <span
              className={cn("relative overflow-hidden rounded-full bg-black-1", {
                "bg-transparent": !picture?.resized,
              })}
            >
              {picture?.resized ? (
                <span
                  className={"block overflow-hidden rounded-full"}
                  style={{
                    background:
                      picture?.color ?? "linear-gradient(rgba(255, 75, 0, 0.6) 0%, rgba(255, 75, 0, 0.25) 100%)",
                  }}
                >
                  <img
                    src={picture?.resized ?? ""}
                    alt="profile-image"
                    className={
                      "size-14 translate-y-[0.5rem] transform rounded-full object-cover object-center max-lg:size-10"
                    }
                  />
                </span>
              ) : (
                <>
                  <IconEmptyUser className="size-14" />
                </>
              )}
              {is_lock ? (
                <span className={"absolute left-0 top-0 flex h-full w-full items-center justify-center bg-black-1/40"}>
                  {!isLoading.enter ? (
                    <IconProfileSelectLock className={"size-6 stroke-on-primary"} />
                  ) : (
                    <Spinner className={"size-6 fill-on-primary"} />
                  )}
                </span>
              ) : null}
            </span>
            <span className={"text-start text-xl/6 max-lg:text-base/5"}>{name}</span>
          </span>
          <span className={"block text-start text-sm/4"}>
            {t("General.age")} {age}
          </span>
          {picture?.resized && (
            <img
              src={picture?.resized ?? ""}
              alt={"profile-image"}
              className={"absolute -bottom-6 -right-6 size-[7.5rem] object-contain max-lg:-bottom-2"}
            />
          )}
        </button>

        <button
          type={"button"}
          aria-label={t("Actions.edit")}
          onClick={handleEditClick}
          className={
            "absolute bottom-4 left-4 flex items-center gap-1 rounded-full bg-on-primary px-2 py-1 text-sm/4 text-black-1 max-lg:text-xs/[0.875rem]"
          }
        >
          {!isLoading.edit ? (
            <IconPencil className={"size-3 fill-black-1 max-lg:size-[0.625rem]"} />
          ) : (
            <Spinner className={"size-3 fill-black-1"} />
          )}
          {t("Actions.edit")}
        </button>
      </div>
    </>
  );
};

export default ProfileCard;
